import React, { useState, useMemo, useCallback } from 'react';
import { debounce } from 'lodash';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import InfoTooltip from 'components/shared/InfoTooltip';
import Select from 'components/shared/select';
import { BetaFeedback } from 'components/BetaProgram';
import EMAILS from 'constants/emails';
import { learningExperiences } from 'constants/learningExperiences';
import LearningExperienceChoices from './LearningExperienceChoices';
import ElearningFileUploader from './ElearningFileUploader';

const DEBOUNCE_CHANGES_DELAY = 300;

const ALWAYS = 'WHEN_NEWER_PACKAGE_VERSION_EXISTS';
const INCOMPLETE =
  'WHEN_EXISTING_REG_IS_INCOMPLETE_AND_NEWER_PACKAGE_VERSION_EXISTS';
const NEVER = 'NEVER';

const restartDropdown = [
  {
    value: ALWAYS,
    text: t('questions.types.elearning.scorm_restart.always'),
  },
  {
    value: INCOMPLETE,
    text: t('questions.types.elearning.scorm_restart.incomplete'),
  },
  {
    value: NEVER,
    text: t('questions.types.elearning.scorm_restart.never'),
  },
];

const Body = ({
  scormId,
  learningExperienceInitialValue,
  finish,
  restartVal,
  canUploadFile,
  handleUpdate,
  publishedVersion,
  filename,
  isUploading,
}) => {
  const [navigation, setNavigation] = useState(finish);
  const [learningExperience, setLearningExperience] = useState(
    learningExperiences.find(experience => {
      return experience.value === learningExperienceInitialValue;
    }),
  );
  const [displayRestartDropdown, setDisplayRestartDropdown] = useState(
    !canUploadFile && publishedVersion !== null,
  );
  const [restart, setRestart] = useState(restartVal || ALWAYS);
  const [displayConfigOptions, setDisplayConfigOptions] = useState(!!filename);

  const debouncedHandleUpdate = useMemo(() => {
    const asyncUpdate = e => {
      handleUpdate({ scorm_config_settings: { ...e } });
    };
    return debounce(asyncUpdate, DEBOUNCE_CHANGES_DELAY);
  }, [handleUpdate]);

  const updateLearningExperience = experience => {
    setLearningExperience(experience);
    debouncedHandleUpdate({
      ...experience.properties,
      finish: navigation,
      restart,
    });
  };

  const updateNavigation = () => {
    const updatedNavigation = !navigation;
    setNavigation(updatedNavigation);
    debouncedHandleUpdate({
      ...learningExperience.properties,
      finish: updatedNavigation,
      restart,
    });
  };

  const setUploadSuccessful = useCallback(() => {
    setDisplayConfigOptions(true);
    setDisplayRestartDropdown(publishedVersion !== null);
    debouncedHandleUpdate();
  }, [debouncedHandleUpdate, publishedVersion]);

  const onRestartChange = e => {
    const newValue = e.target.value;
    setRestart(newValue);
    debouncedHandleUpdate({
      ...learningExperience.properties,
      finish: navigation,
      restart: newValue,
    });
  };

  return (
    <div className="EditorElement-body Elearning">
      <div className="Elearning-block">
        <Text
          className="Elearning-label"
          variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
        >
          {t('questions.types.elearning.file')}{' '}
          <span className="c-danger-red">*</span>
        </Text>
        <div>
          <ElearningFileUploader
            scormId={scormId}
            setUploadSuccessful={setUploadSuccessful}
            canUploadFile={canUploadFile}
            filename={filename}
            isUploading={isUploading}
          />
        </div>
      </div>
      {displayRestartDropdown && (
        <div className="Elearning-block">
          <Text
            className="Elearning-label"
            variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
            component="label"
            htmlFor="restart"
          >
            {t('questions.types.elearning.scorm_restart.title')}
          </Text>
          <Select
            className="Elearning-select"
            name="restart"
            id="restart"
            value={restart}
            onChange={onRestartChange}
            fullWidth
          >
            {restartDropdown.map(restartOption => (
              <option value={restartOption.value} key={restartOption.value}>
                {restartOption.text}
              </option>
            ))}
          </Select>
        </div>
      )}
      {displayConfigOptions && (
        <>
          <div className="Elearning-block">
            <Text
              className="Elearning-label"
              variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
            >
              {t('questions.types.elearning.learning_experience')}
            </Text>
            <LearningExperienceChoices
              learningExperience={learningExperience}
              updateLearningExperience={updateLearningExperience}
            />
          </div>
          <div className="Elearning-block">
            <Text
              className="Elearning-label"
              variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
            >
              {t('questions.types.elearning.navigation')}
            </Text>
            <div className="FormElement-row">
              <label htmlFor="NavigationBar">
                <input
                  id="NavigationBar"
                  name="NavigationBar"
                  type="checkbox"
                  className="FormElement-checkbox"
                  onChange={updateNavigation}
                  checked={navigation}
                />
                <Text variant={TEXT_VARIANTS.BODY}>
                  {t('questions.types.elearning.navigation_label')}
                </Text>
              </label>
              <InfoTooltip
                className="info-icon c-midnight-50"
                tooltip={t('questions.types.elearning.navigation_label_info')}
                placement="bottom"
              />
            </div>
          </div>
        </>
      )}
      <div className="Elearning-block">
        <Text
          className="Elearning-label"
          variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
        >
          {t('questions.types.elearning.completion')}
        </Text>
        <Text variant={TEXT_VARIANTS.BODY} component="div">
          {t('questions.types.elearning.completion_value')}
        </Text>
      </div>
      <div className="Elearning-block">
        <Text
          className="Elearning-label"
          variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
        >
          {t('questions.types.elearning.grading')}
        </Text>
        <Text variant={TEXT_VARIANTS.BODY} component="div">
          {t('questions.types.elearning.grading_value')}
          <InfoTooltip
            className="info-icon c-midnight-50"
            tooltip={t('questions.types.elearning.grading_info')}
            placement="bottom"
          />
        </Text>
      </div>
      <div className="Elearning-footer">
        <BetaFeedback
          primary
          betaFeatureName="Weave eLearning"
          recipient={EMAILS.ELEARNING_FEEDBACK}
        />
      </div>
    </div>
  );
};

export default Body;
